export interface FileUploadFailure {
  fileName: string;
  message: string;
}

/**
 * Builds the summary shown when one or more files could not be queued for
 * AI extraction. `queuedCount` is how many of the selected files succeeded.
 */
export function describeUploadFailures(
  failures: FileUploadFailure[],
  queuedCount: number,
): string {
  const total = failures.length + queuedCount;
  const details = failures
    .map((failure) => `${failure.fileName} (${failure.message})`)
    .join("; ");
  const lead =
    queuedCount === 0
      ? `${failures.length === 1 ? "The file" : `All ${total} files`} could not be queued for extraction: ${details}.`
      : `${failures.length} of ${total} files could not be queued for extraction: ${details}.`;
  if (queuedCount === 0) return lead;
  return `${lead} The other ${queuedCount === 1 ? "file was" : `${queuedCount} files were`} queued and will appear on the AI Extractions page.`;
}

/** Readable error message from an ApiResponse-like failure (or a thrown error). */
export function getUploadErrorMessage(
  failure:
    | { status?: number; message?: string | { message: string } }
    | Error
    | null
    | undefined,
): string {
  if (failure instanceof Error) return failure.message || "Upload failed";
  const message = failure?.message;
  if (typeof message === "string" && message) return message;
  if (message && typeof message === "object" && message.message) {
    return message.message;
  }
  if (failure?.status) return `Request failed with status ${failure.status}`;
  return "Upload failed";
}

/** File types accepted for AI extraction (matches the file input `accept`). */
export const EXTRACTION_FILE_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xlsx",
  ".xls",
  ".png",
  ".msg",
  ".eml",
];
/** Initial materials extraction on an existing deal accepts PDFs only. */
export const PDF_ONLY_EXTENSIONS = [".pdf"];

/** Download name for the tenancy schedule template (no query string). */
export const TENANCY_TEMPLATE_FILE_NAME = "tenancy-schedule-template.xlsx";

const hasAcceptedExtension = (file: File, extensions: string[]) => {
  const name = file.name.toLowerCase();
  return extensions.some((ext) => name.endsWith(ext.toLowerCase()));
};

/**
 * Splits files into those matching the accepted extensions and those that
 * don't. Drag-and-drop bypasses the input's `accept` attribute, so dropped
 * (and browsed) files are validated here.
 */
export function partitionFilesByExtension(
  files: File[],
  extensions: string[],
): { accepted: File[]; rejected: File[] } {
  const accepted: File[] = [];
  const rejected: File[] = [];
  for (const file of files) {
    (hasAcceptedExtension(file, extensions) ? accepted : rejected).push(file);
  }
  return { accepted, rejected };
}

const isSameFile = (a: File, b: File) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

/** Appends new files to the selection, skipping files already selected. */
export function mergeSelectedFiles(existing: File[], incoming: File[]): File[] {
  const merged = [...existing];
  for (const file of incoming) {
    if (!merged.some((selected) => isSameFile(selected, file))) {
      merged.push(file);
    }
  }
  return merged;
}
